import { useState } from 'react'
import { NavLink, Route, Routes } from 'react-router-dom'
import './membership.css'
import { type Lookups, type Summary } from './api'
import { Kpi, useJson } from './common'
import { MembersView, MembershipsView } from './grids'
import { MemberRecordPage, MembershipRecordPage } from './records'
import { IntegrationsView } from './integrations'
import { downloadMembershipWorkbook, EXPORT_VARIANTS, type MembershipExportVariant } from './exportXlsx'

// "Download XLS" (PR C′, 2026-09-22): one workbook with everything — members, memberships, payments,
// history, discrepancies, pairings and change lines — fetched in pages and built in the browser.
// The dropdown (2026-10-23) picks the layout: Default, or "Dave Boddy's Variant 1" (see exportXlsx.ts).
function DownloadXlsButton({ summary }: { summary: Summary | null }) {
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [variant, setVariant] = useState<MembershipExportVariant>('default')
  const current = EXPORT_VARIANTS.find((v) => v.key === variant) ?? EXPORT_VARIANTS[0]
  const run = async () => {
    setBusy(true); setError(null); setProgress('Starting…')
    try { await downloadMembershipWorkbook(summary, setProgress, variant); setProgress('Downloaded.') }
    catch (e) { setError(String((e as Error)?.message ?? e)); setProgress(null) }
    finally { setBusy(false); setTimeout(() => setProgress(null), 6000) }
  }
  return (
    <span className="mbr-download">
      <select value={variant} onChange={(e) => setVariant(e.target.value as MembershipExportVariant)} disabled={busy} aria-label="Download layout" title={current.title}>
        {EXPORT_VARIANTS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
      </select>
      <button type="button" className="btn-secondary" disabled={busy} onClick={() => void run()} title={current.title}>
        {busy ? 'Preparing…' : 'Download XLS'}
      </button>
      {progress && <span className="mbr-note"> {progress}</span>}
      {error && <span className="mbr-error"> {error}</span>}
    </span>
  )
}

/**
 * Membership tab (PR A, 2026-09-22) — docs/MEMBERSHIP_BUILD_PLAN.md.
 *   /membership                      Members (default)
 *   /membership/memberships          Memberships
 *   /membership/integrations         Integrations (Sync from Ontrack, discrepancies, history …)
 *   /membership/members/:id          a member, full width, with sub-tabs
 *   /membership/memberships/:id      a membership, full width, with sub-tabs
 * Visible to SG-Reports-Engagement and Reports admins; every API route says the same.
 */
export function MembershipScreen({ me }: { me: { isEngagement: boolean; isAdmin: boolean } }) {
  const enabled = me.isEngagement || me.isAdmin
  const summary = useJson<Summary>(enabled ? '/api/membership/summary' : null)
  const lookups = useJson<Lookups>(enabled ? '/api/membership/lookups' : null)

  if (!enabled) {
    return (
      <div className="budget-pnl">
        <h2>Membership</h2>
        <p>This area is limited to members of <code>SG-Reports-Engagement</code> and Reports admins. Ask Chris Flynn for access.</p>
      </div>
    )
  }
  const c = summary.data?.counts
  const pill = ({ isActive }: { isActive: boolean }) => 'mbr-pill' + (isActive ? ' active' : '')

  return (
    <div className="mbr">
      <div className="mbr-head">
        <div>
          <p className="budget-sub">Members and memberships from Ontrack, paired to Donorfy and GoCardless, with every change audited. Pulled from Ontrack every night at 05:30 and whenever someone presses Sync on the Integrations tab; nothing is ever written to Ontrack, Donorfy or GoCardless automatically. Visible to <code>SG-Reports-Engagement</code> and Reports admins.</p>
          {summary.error && <p className="mbr-error">{summary.error}</p>}
        </div>
        <nav className="mbr-pills" aria-label="Membership tabs">
          <NavLink to="/membership" end className={pill}>Members</NavLink>
          <NavLink to="/membership/memberships" className={pill}>Memberships</NavLink>
          <NavLink to="/membership/integrations" className={pill}>Integrations</NavLink>
          <DownloadXlsButton summary={summary.data} />
        </nav>
      </div>

      <Routes>
        <Route index element={
          <>
            {c && (
              <div className="mbr-kpis">
                <Kpi label="Current members" value={c.members_current.toLocaleString('en-GB')} sub={`${c.members_inactive.toLocaleString('en-GB')} deceased, gone away or archived (hidden by default)`} />
                <Kpi label="Current memberships" value={c.memberships_current.toLocaleString('en-GB')} sub={`${c.memberships_dd.toLocaleString('en-GB')} by direct debit`} />
                <Kpi label="Payments held" value={c.payments.toLocaleString('en-GB')} sub="Ontrack fees plus GoCardless collections" />
                <Kpi label="Discrepancies with Ontrack" value={c.members_discrepant.toLocaleString('en-GB')} sub="members with a shared field that differs" />
                <Kpi label="Gift Aid by campaign" value="—" sub="arrives with the Gift Aid build" />
              </div>
            )}
            <MembersView lookups={lookups.data} />
          </>
        } />
        <Route path="memberships" element={<MembershipsView lookups={lookups.data} />} />
        <Route path="integrations" element={<IntegrationsView summary={summary.data} reloadSummary={() => { summary.reload(); lookups.reload() }} />} />
        <Route path="members/:id" element={<MemberRecordPage />} />
        <Route path="memberships/:id" element={<MembershipRecordPage />} />
      </Routes>
    </div>
  )
}
